<script setup>
import * as echarts from 'echarts'
import { gsap } from 'gsap'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'

const props = defineProps({
  events: { type: Array, required: true },
  routeData: { type: Object, required: true },
  activeId: { type: String, required: true }
})

const emit = defineEmits(['select'])
const stageRef = ref(null)
const chartRef = ref(null)
const mode = ref('schematic')
const loading = ref(true)
const mapError = ref('')
const statusText = computed(() => {
  if (mapError.value) return '天地图未加载'
  return mode.value === 'tianditu' ? '天地图在线底图 · 滚轮缩放' : '纯黑路线底板'
})
const routeCoordinates = props.routeData.features[0].geometry.coordinates
const animationState = { progress: 0 }
const initialMapView = { center: [108.6, 31.1], zoom: 5 }
const eventLabelMeta = {
  'ruijin-departure': { name: '瑞金', position: 'right' },
  'yudu-departure': { name: '于都', position: 'left' },
  'blockade-breakthrough': { name: '前三道封锁线', position: 'top' },
  'xiangjiang-battle': { name: '湘江', position: 'left' },
  'tongdao-meeting': { name: '通道', position: 'left' },
  'liping-meeting': { name: '黎平', position: 'right' },
  'wujiang-crossing': { name: '乌江', position: 'left' },
  'zunyi-meeting': { name: '遵义', position: 'right' },
  'chishui-campaign': { name: '四渡赤水', position: 'top' },
  'jinsha-crossing': { name: '金沙江', position: 'right' },
  'huili-meeting': { name: '会理', position: 'right' },
  'dadu-crossing': { name: '大渡河', position: 'left' },
  'luding-bridge': { name: '泸定桥', position: 'right' },
  'jiajin-crossing': { name: '夹金山', position: 'left' },
  'maogong-reunion': { name: '懋功', position: 'right' },
  'grassland-crossing': { name: '草地', position: 'right' },
  'lazikou-battle': { name: '腊子口', position: 'left' },
  hadapu: { name: '哈达铺', position: 'right' },
  'bangluo-meeting': { name: '榜罗镇', position: 'left' },
  'liupanshan-crossing': { name: '六盘山', position: 'left' },
  'wuqi-arrival': { name: '吴起镇', position: 'right' }
}
let chart
let map
let resizeObserver
let routeTween
let lastWheelAt = 0
let suppressNextClick = false
const mapResizeTimers = []
const dragState = {
  active: false,
  moved: false,
  pointerId: null,
  lastX: 0,
  lastY: 0
}

function representativeCoordinate(location) {
  if (!location?.geometry) return null
  const { type, coordinates } = location.geometry
  if (type === 'Point') return coordinates
  if (type === 'MultiPoint' || type === 'LineString') return coordinates[Math.floor(coordinates.length / 2)]
  return null
}

function loadTianditu(token) {
  return new Promise((resolve, reject) => {
    if (window.T?.Map) return resolve()

    let settled = false
    const timeoutId = window.setTimeout(() => {
      if (settled) return
      settled = true
      document.querySelector('script[data-tianditu]')?.remove()
      reject(new Error(`连接天地图超时，请检查网络及Key白名单是否包含 ${window.location.hostname}`))
    }, 15000)

    const finish = () => {
      if (settled) return
      settled = true
      window.clearTimeout(timeoutId)
      if (window.T?.Map) resolve()
      else reject(new Error('天地图脚本已下载，但API未正确初始化，请刷新页面重试'))
    }

    const fail = (script) => {
      if (settled) return
      settled = true
      window.clearTimeout(timeoutId)
      script?.remove()
      reject(new Error(`无法下载天地图API，请检查网络、浏览器端Key及白名单 ${window.location.hostname}`))
    }

    const existing = document.querySelector('script[data-tianditu]')
    if (existing) {
      existing.addEventListener('load', finish, { once: true })
      existing.addEventListener('error', () => fail(existing), { once: true })
      return
    }
    const script = document.createElement('script')
    script.dataset.tianditu = 'true'
    script.src = `https://api.tianditu.gov.cn/api?v=4.0&tk=${encodeURIComponent(token)}`
    script.onload = finish
    script.onerror = () => fail(script)
    document.head.appendChild(script)
  })
}

function projectCoordinate([lon, lat]) {
  if (map && window.T?.LngLat) {
    const point = map.lngLatToContainerPoint(new window.T.LngLat(lon, lat))
    return [point.x, point.y]
  }
  const rect = chartRef.value.getBoundingClientRect()
  const bounds = { minLon: 101.3, maxLon: 116.8, minLat: 24.8, maxLat: 37.7 }
  const x = ((lon - bounds.minLon) / (bounds.maxLon - bounds.minLon)) * rect.width
  const y = ((bounds.maxLat - lat) / (bounds.maxLat - bounds.minLat)) * rect.height
  return [x, y]
}

function projectedRoute() {
  const target = Math.max(2, Math.ceil(routeCoordinates.length * animationState.progress))
  return routeCoordinates.slice(0, target).map(projectCoordinate)
}

function buildOption() {
  const rect = chartRef.value.getBoundingClientRect()
  const route = projectedRoute()
  const fullRoute = routeCoordinates.map(projectCoordinate)
  const points = props.events
    .map((event) => {
      const coordinate = representativeCoordinate(event.location)
      if (!coordinate) return null
      const labelMeta = eventLabelMeta[event.id]
      return {
        value: projectCoordinate(coordinate),
        eventId: event.id,
        title: event.title,
        labelName: labelMeta?.name ?? event.title,
        date: event.startDate,
        role: event.location?.displayRole,
        selected: event.id === props.activeId,
        symbolOffset: labelMeta?.symbolOffset ?? [0, 0],
        label: { position: labelMeta?.position ?? 'top' }
      }
    })
    .filter(Boolean)

  return {
    animation: false,
    grid: { left: 0, top: 0, right: 0, bottom: 0, containLabel: false },
    xAxis: { type: 'value', min: 0, max: rect.width, show: false },
    yAxis: { type: 'value', min: 0, max: rect.height, inverse: true, show: false },
    tooltip: {
      trigger: 'item',
      appendToBody: true,
      borderWidth: 1,
      borderColor: 'rgba(206, 162, 76, .45)',
      backgroundColor: 'rgba(8, 12, 17, .94)',
      textStyle: { color: '#f5ead4' },
      formatter: (params) => params.data?.title
        ? `<strong>${params.data.title}</strong><br><span style="color:#b7aa96">${params.data.date}</span>`
        : ''
    },
    series: [
      {
        name: '中央红军长征主线基线',
        type: 'line',
        coordinateSystem: 'cartesian2d',
        data: fullRoute,
        showSymbol: false,
        silent: true,
        lineStyle: {
          color: 'rgba(208, 180, 126, .42)',
          width: 1.4,
          type: 'dashed'
        },
        z: 1
      },
      {
        name: '中央红军长征主线',
        type: 'line',
        coordinateSystem: 'cartesian2d',
        data: route,
        showSymbol: false,
        silent: true,
        lineStyle: {
          color: '#d64038',
          width: 3.2,
          opacity: 0.92,
          shadowBlur: 14,
          shadowColor: 'rgba(214, 64, 56, .48)'
        },
        z: 3
      },
      {
        name: '路线辉光',
        type: 'line',
        coordinateSystem: 'cartesian2d',
        data: route,
        showSymbol: false,
        silent: true,
        lineStyle: { color: 'rgba(255, 200, 120, .35)', width: 8, opacity: 0.22 },
        z: 2
      },
      {
        name: '事件节点',
        type: 'scatter',
        coordinateSystem: 'cartesian2d',
        data: points,
        symbolSize: (value, params) => params.data.selected ? 18 : 10,
        itemStyle: {
          color: (params) => params.data.selected ? '#ffd181' : '#f2eadc',
          borderColor: '#9d2526',
          borderWidth: 2,
          shadowBlur: 12,
          shadowColor: 'rgba(214, 64, 56, .65)'
        },
        emphasis: { scale: 1.35 },
        label: {
          show: true,
          formatter: (params) => params.data.labelName,
          distance: 8,
          color: '#f4e7d0',
          fontSize: 11,
          fontWeight: 600,
          backgroundColor: 'rgba(7, 12, 15, .82)',
          borderColor: 'rgba(196, 151, 70, .32)',
          borderWidth: 1,
          borderRadius: 2,
          padding: [4, 6]
        },
        labelLayout: {
          hideOverlap: false,
          moveOverlap: 'shiftY'
        },
        z: 6
      },
      {
        name: '当前节点',
        type: 'effectScatter',
        coordinateSystem: 'cartesian2d',
        data: points.filter((point) => point.selected),
        symbolSize: 18,
        rippleEffect: { scale: 3.2, brushType: 'stroke' },
        itemStyle: { color: '#ffd181' },
        silent: true,
        z: 7
      }
    ]
  }
}

function renderChart() {
  if (!chart || !chartRef.value) return
  chart.resize()
  chart.setOption(buildOption(), { notMerge: true, lazyUpdate: true })
}

function waitForStableStage() {
  return new Promise((resolve) => {
    let previousWidth = 0
    let previousHeight = 0
    let stableFrames = 0
    let attempts = 0

    const check = () => {
      const rect = stageRef.value?.getBoundingClientRect()
      attempts += 1
      if (rect && rect.width >= 480 && rect.height >= 300) {
        const unchanged = Math.abs(rect.width - previousWidth) < 1
          && Math.abs(rect.height - previousHeight) < 1
        stableFrames = unchanged ? stableFrames + 1 : 0
        previousWidth = rect.width
        previousHeight = rect.height
      }

      if (stableFrames >= 2 || attempts >= 90) resolve()
      else window.requestAnimationFrame(check)
    }

    window.requestAnimationFrame(check)
  })
}

function syncMapSize() {
  if (!map || mode.value !== 'tianditu') return
  const center = map.getCenter?.()
  const zoom = map.getZoom?.()
  map.checkResize?.()
  if (center && Number.isFinite(zoom)) map.centerAndZoom(center, zoom)
  renderChart()
}

function scheduleMapSizeSync() {
  ;[0, 120, 400, 1000].forEach((delay) => {
    mapResizeTimers.push(window.setTimeout(syncMapSize, delay))
  })
}

function zoomIn() {
  map?.zoomIn?.()
}

function zoomOut() {
  map?.zoomOut?.()
}

function resetMapView() {
  if (!map || !window.T?.LngLat) return
  map.centerAndZoom(
    new window.T.LngLat(...initialMapView.center),
    initialMapView.zoom
  )
}

function handleMapWheel(event) {
  if (!map || mode.value !== 'tianditu') return
  event.preventDefault()

  const now = Date.now()
  if (now - lastWheelAt < 160) return
  lastWheelAt = now

  const currentZoom = map.getZoom?.()
  if (window.T?.Point && Number.isFinite(currentZoom)) {
    const focus = map.containerPointToLngLat(
      new window.T.Point(event.offsetX, event.offsetY)
    )
    const nextZoom = currentZoom + (event.deltaY < 0 ? 1 : -1)
    map.centerAndZoom(focus, nextZoom)
    return
  }

  if (event.deltaY < 0) zoomIn()
  else zoomOut()
}

function handlePointerDown(event) {
  if (!map || mode.value !== 'tianditu' || event.button !== 0) return
  dragState.active = true
  dragState.moved = false
  dragState.pointerId = event.pointerId
  dragState.lastX = event.clientX
  dragState.lastY = event.clientY
  chartRef.value?.setPointerCapture?.(event.pointerId)
  chartRef.value?.classList.add('is-dragging')
}

function handlePointerMove(event) {
  if (!dragState.active || !map || !window.T?.Point) return
  const deltaX = event.clientX - dragState.lastX
  const deltaY = event.clientY - dragState.lastY
  if (Math.abs(deltaX) + Math.abs(deltaY) < 2) return

  event.preventDefault()
  dragState.moved = true
  dragState.lastX = event.clientX
  dragState.lastY = event.clientY
  map.panBy(new window.T.Point(-deltaX, -deltaY))
}

function finishPointerDrag() {
  if (!dragState.active) return
  suppressNextClick = dragState.moved
  if (suppressNextClick) {
    window.setTimeout(() => { suppressNextClick = false }, 0)
  }
  dragState.active = false
  chartRef.value?.classList.remove('is-dragging')
  if (dragState.pointerId !== null && chartRef.value?.hasPointerCapture?.(dragState.pointerId)) {
    chartRef.value.releasePointerCapture(dragState.pointerId)
  }
  dragState.pointerId = null
}

function handleChartClick(event) {
  if (suppressNextClick || !chartRef.value) return
  const rect = chartRef.value.getBoundingClientRect()
  const clickPoint = [event.clientX - rect.left, event.clientY - rect.top]
  let nearest = null

  props.events.forEach((item) => {
    const coordinate = representativeCoordinate(item.location)
    if (!coordinate) return
    const point = projectCoordinate(coordinate)
    const offset = eventLabelMeta[item.id]?.symbolOffset ?? [0, 0]
    const distance = Math.hypot(
      clickPoint[0] - point[0] - offset[0],
      clickPoint[1] - point[1] - offset[1]
    )
    if (!nearest || distance < nearest.distance) nearest = { id: item.id, distance }
  })

  if (nearest?.distance <= 24) emit('select', nearest.id)
}

async function initMap() {
  const token = import.meta.env.VITE_TDT_TOKEN
  if (!token || token.includes('请填写')) {
    mode.value = 'schematic'
    loading.value = false
    return
  }
  try {
    await loadTianditu(token)
    map = new window.T.Map(stageRef.value)
    map.centerAndZoom(
      new window.T.LngLat(...initialMapView.center),
      initialMapView.zoom
    )
    map.enableScrollWheelZoom()
    map.addEventListener('move', renderChart)
    map.addEventListener('zoom', renderChart)
    mode.value = 'tianditu'
    scheduleMapSizeSync()
  } catch (error) {
    console.warn('天地图加载失败，已切换为纯黑底板。', error)
    mapError.value = error instanceof Error ? error.message : String(error)
    mode.value = 'schematic'
  } finally {
    loading.value = false
  }
}

function restartRouteAnimation() {
  routeTween?.kill()
  animationState.progress = 0.03
  routeTween = gsap.to(animationState, {
    progress: 1,
    duration: 7.5,
    ease: 'power1.inOut',
    onUpdate: renderChart
  })
}

onMounted(async () => {
  await nextTick()
  await waitForStableStage()
  chart = echarts.init(chartRef.value, null, { renderer: 'canvas' })
  chartRef.value.addEventListener('click', handleChartClick)
  chartRef.value.addEventListener('wheel', handleMapWheel, { passive: false })
  chartRef.value.addEventListener('pointerdown', handlePointerDown)
  chartRef.value.addEventListener('pointermove', handlePointerMove)
  chartRef.value.addEventListener('pointerup', finishPointerDrag)
  chartRef.value.addEventListener('pointercancel', finishPointerDrag)
  await initMap()
  renderChart()
  restartRouteAnimation()
  resizeObserver = new ResizeObserver(() => {
    syncMapSize()
    renderChart()
  })
  resizeObserver.observe(stageRef.value)
})

watch(() => props.activeId, () => renderChart())

onBeforeUnmount(() => {
  routeTween?.kill()
  mapResizeTimers.forEach((timerId) => window.clearTimeout(timerId))
  resizeObserver?.disconnect()
  chartRef.value?.removeEventListener('wheel', handleMapWheel)
  chartRef.value?.removeEventListener('click', handleChartClick)
  chartRef.value?.removeEventListener('pointerdown', handlePointerDown)
  chartRef.value?.removeEventListener('pointermove', handlePointerMove)
  chartRef.value?.removeEventListener('pointerup', finishPointerDrag)
  chartRef.value?.removeEventListener('pointercancel', finishPointerDrag)
  chart?.dispose()
  if (map) {
    map.removeEventListener('move', renderChart)
    map.removeEventListener('zoom', renderChart)
    map.clearOverLays?.()
  }
})
</script>

<template>
  <div class="map-stage" :class="`mode-${mode}`">
    <div ref="stageRef" class="tdt-layer"></div>
    <div v-if="mode === 'schematic'" class="schematic-layer" aria-hidden="true"></div>
    <div ref="chartRef" class="chart-layer" role="img" aria-label="中央红军长征路线与关键事件地图"></div>
    <div class="map-status">
      <span class="status-dot"></span>
      {{ statusText }}
    </div>
    <div v-if="loading" class="map-loading">正在初始化地图…</div>
    <button class="replay-route" type="button" @click="restartRouteAnimation">重播路线</button>
    <div v-if="mode === 'tianditu'" class="map-zoom-controls" aria-label="地图缩放控制">
      <button type="button" aria-label="放大地图" title="放大地图" @click.stop="zoomIn">＋</button>
      <button type="button" aria-label="缩小地图" title="缩小地图" @click.stop="zoomOut">−</button>
      <button class="reset-view" type="button" title="恢复全国视图" @click.stop="resetMapView">复位</button>
    </div>
  </div>
</template>
